/**
 * **One readout across several charts** — the crosshair on Tonaż puts the crosshair on Sen, on the
 * same week, without either chart knowing the other exists.
 *
 * ── Why this exists ───────────────────────────────────────────────────────────────────────────
 * Owner, 31 August 2026: the question a trainer opens Trendy to answer is "frekwencja dropped —
 * did sleep drop in the same week?". Two charts that each answer only under their own pointer make
 * that a memory exercise. Hovering one and reading both is the feature.
 *
 * ── Why a subscription bus and not a piece of React state ─────────────────────────────────────
 * The obvious build is `useState` on the tab: the hovered instant goes up, both charts read it
 * down. That re-renders the WHOLE TAB on every frame of every pointer move — the section headings,
 * the volume table, the silhouette, all of it — which is precisely the "no re-render of the whole
 * tab because the cursor twitched" the owner ruled out.
 *
 * So the value never enters React's render path on the publishing side. A follower subscribes,
 * and only a follower re-renders. The provider's context value is created once and never changes,
 * so no consumer re-renders because of the provider either.
 *
 * ── Feedback loops are prevented by OWNERSHIP, not by comparison ──────────────────────────────
 * Every chart has an id. A frame carries the id of the chart the pointer is actually over, and a
 * chart ignores every frame stamped with its own id. Without that the follower's `setTooltipData`
 * would publish again on the next frame and the two charts would push each other forever. A
 * value-equality guard would ALSO stop it most of the time, which is why it is worth saying that
 * this does not rely on one: two charts with different data rows would round to different instants
 * and the guard would never fire.
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';

/** What one chart tells the others: which instant is under the pointer, or that none is. */
export interface ReadoutFrame {
  /** The chart the pointer is over. A chart ignores frames it published itself. */
  readonly source: string;
  /** Milliseconds since the epoch, or `null` when the pointer left the plot. */
  readonly at: number | null;
}

interface ReadoutBus {
  readonly publish: (frame: ReadoutFrame) => void;
  /** Returns the unsubscribe. */
  readonly subscribe: (listener: (frame: ReadoutFrame) => void) => () => void;
}

const ReadoutSyncContext = createContext<ReadoutBus | null>(null);

/**
 * Wrap the charts that share an x axis. Charts outside a provider simply do not sync — the hook
 * below returns `null` and every chart is on its own, which is the right behaviour for a lone
 * chart and means no caller has to opt out.
 */
export function ReadoutSyncProvider({ children }: { readonly children: ReactNode }) {
  const listeners = useRef(new Set<(frame: ReadoutFrame) => void>());

  const publish = useCallback((frame: ReadoutFrame) => {
    for (const listener of listeners.current) listener(frame);
  }, []);

  const subscribe = useCallback((listener: (frame: ReadoutFrame) => void) => {
    listeners.current.add(listener);
    return () => {
      listeners.current.delete(listener);
    };
  }, []);

  // Stable for the provider's whole life: both fields are a `useCallback` over a ref, so no
  // consumer re-renders because this object changed. That is the point of the whole module.
  const bus = useMemo<ReadoutBus>(() => ({ publish, subscribe }), [publish, subscribe]);

  return <ReadoutSyncContext.Provider value={bus}>{children}</ReadoutSyncContext.Provider>;
}

export function useReadoutSync(): ReadoutBus | null {
  return useContext(ReadoutSyncContext);
}
